import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Upload,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { ArrowLeftOutlined, CompressOutlined, ExpandOutlined, PlusOutlined } from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useAppConfig from "hooks/access-control/useAppConfig";
import apiClient from "utils/apiClient";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import { professionSectorService } from "services/professionSectorService";
import { STORAGE_BASE_URL } from "constants";
import GooglePlacesTagsSelect from "components/common/GooglePlacesTagsSelect";
import GooglePlacesAutocompleteInput from "components/common/GooglePlacesAutocompleteInput";
import { locationFieldsFromPlaceDetails, regionSummaryFromPlaceDetails } from "utils/googlePlaces";

const { Text } = Typography;

const extractFileId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    return (
      value.fileId ||
      value.fid ||
      value.id ||
      value.file_id ||
      value.file?.fileId ||
      value.file?.id ||
      null
    );
  }
  return null;
};

const resolveFileUrl = (value) => {
  const fid = extractFileId(value);
  if (!fid) return null;
  const str = String(fid);
  if (str.includes("://")) return str;
  return `${STORAGE_BASE_URL}/files/${str}`;
};

const isImageUrl = (url) => /\.(png|jpe?g|webp|gif|bmp|svg)(\?|$)/i.test(String(url || ""));
const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(String(url || ""));

export default function HelperProfessionProfileFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { helperId, professionProfileId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const { appConfig } = useAppConfig();
  const googleMapsApiKey = appConfig?.googleMapsApiKey || appConfig?.googleMapsApiMatrixKey || "";

  const mode = professionProfileId ? "edit" : "create";
  const presetType = searchParams.get("type") || null; // GENERAL | SPECIALIZED

  const seededProfile = location?.state?.profile || null;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(mode === "edit" && !seededProfile);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fullWidth, setFullWidth] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");

  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [serviceOfferingsLoading, setServiceOfferingsLoading] = useState(false);
  const [serviceOfferingOptions, setServiceOfferingOptions] = useState([]);

  const [signedUrlByFileId, setSignedUrlByFileId] = useState({});
  const [documentsFiles, setDocumentsFiles] = useState([]);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [documentsUseObjects, setDocumentsUseObjects] = useState(true);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const scrollRef = useRef(null);
  const pendingSectionRef = useRef(null);
  const pendingSectionUntilRef = useRef(0);

  const title = useMemo(() => {
    if (mode === "create") return "Add Profession Profile";
    return "Edit Profession Profile";
  }, [mode]);

  const extractBackendMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) {
      try {
        const parsed = JSON.parse(data);
        if (parsed?.message) return parsed.message;
      } catch {
        return data;
      }
    }
    if (data?.message) return data.message;
    return err?.message || "Failed to load profession profile";
  };

  const fetchSignedUrlForFileId = async (fileId) => {
    const resolved = extractFileId(fileId);
    if (!resolved) return null;
    if (signedUrlByFileId[resolved]) return signedUrlByFileId[resolved];
    const endpoint = `/storage-service/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(
      String(resolved),
    )}`;
    try {
      console.log("[helper-professions-form] signed url request", { fileId: resolved, endpoint });
      const response = await apiClient.get(endpoint);
      console.log("[helper-professions-form] signed url response", { fileId: resolved, data: response?.data });
      const data = response?.data;
      const signedUrl =
        (typeof data === "string" && data.trim()) ||
        (Array.isArray(data) && data[0]) ||
        (Array.isArray(data?.signedUrls) && data.signedUrls[0]) ||
        (typeof data?.url === "string" && data.url) ||
        null;
      if (signedUrl) setSignedUrlByFileId((prev) => ({ ...prev, [resolved]: signedUrl }));
      return signedUrl;
    } catch (err) {
      console.error("[helper-professions-form] signed url fetch failed", { fileId: resolved, err });
      return null;
    }
  };

  const openAttachmentPreview = async (fileId, label = "Attachment") => {
    const resolved = extractFileId(fileId);
    if (!resolved) return;
    let url = resolveFileUrl(resolved);
    const signed = await fetchSignedUrlForFileId(resolved);
    if (signed) url = signed;
    if (!url) return;
    if (isPdfUrl(url) || (!isImageUrl(url) && url.includes("://"))) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setPreviewTitle(label);
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const renderAttachmentValue = (fileId, label = "Attachment") => {
    const resolvedFileId = extractFileId(fileId);
    if (!resolvedFileId) return <Text type="secondary">No file uploaded</Text>;
    const cached = signedUrlByFileId?.[String(resolvedFileId)] || null;
    const url = cached || resolveFileUrl(resolvedFileId);
    if (!url) return <Text type="secondary">No preview available</Text>;

    if (isImageUrl(url)) {
      return (
        <button
          type="button"
          onClick={() => openAttachmentPreview(resolvedFileId, label)}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "pointer",
          }}
        >
          <img
            src={url}
            alt={label}
            style={{
              width: 72,
              height: 72,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          />
        </button>
      );
    }

    return (
      <Tag
        color="default"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => openAttachmentPreview(resolvedFileId, label)}
      >
        {isPdfUrl(url) ? "PDF" : "FILE"} • {String(resolvedFileId).slice(0, 8)}
      </Tag>
    );
  };

  const ensureUploaderId = () => user?.id;

  const syncDocumentsField = (fileList) => {
    const items = (Array.isArray(fileList) ? fileList : [])
      .map((f) => {
        const fileId = extractFileId(f?.fileId || f?.uid);
        if (!fileId) return null;
        if (!documentsUseObjects) return String(fileId);
        const caption = String(f?.caption || f?.name || "").trim() || null;
        // Backend expects HelperDocumentDTO { id, caption }
        return { id: String(fileId), ...(caption ? { caption } : {}) };
      })
      .filter(Boolean);
    form.setFieldValue("documents", items);
  };

  const loadProfile = async () => {
    if (mode !== "edit") return;
    if (!professionProfileId) return;
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line no-console
      console.log("[helper-professions-form] fetch profile", { professionProfileId, helperId });
      const res = await olreadiaService.professionProfiles.get(professionProfileId);
      console.log("[helper-professions-form] getById response", res);
      const docs = Array.isArray(res?.documents) ? res.documents : [];
      const docsAreObjects = docs.some((d) => d && typeof d === "object");
      setDocumentsUseObjects(docsAreObjects);
      form.setFieldsValue({
        id: res?.id,
        profileType: res?.profileType,
        sectorCode: res?.sector?.code || res?.sectorCode || "",
        profileName: res?.profileName || "",
        profileSummary: res?.profileSummary || "",
        description: res?.description || "",
        experience: res?.experience || "",
        previousRegionsWorkedIn: Array.isArray(res?.previousRegionsWorkedIn) ? res.previousRegionsWorkedIn : [],
        references: Array.isArray(res?.references) ? res.references : [],
        workExperienceHistory: Array.isArray(res?.workExperienceHistory) ? res.workExperienceHistory : [],
        education: Array.isArray(res?.education) ? res.education : [],
        educationLevel: res?.educationLevel || undefined,
        documents: docs,
        certifications: Array.isArray(res?.certifications) ? res.certifications : [],
        interestedServices: Array.isArray(res?.interestedServices)
          ? res.interestedServices.map((s) => s?.code || s?.name || s?.id).filter(Boolean)
          : [],
        interestedLocations: Array.isArray(res?.interestedLocations) ? res.interestedLocations : [],
        interestedShifts: Array.isArray(res?.interestedShifts) ? res.interestedShifts : [],
      });
      const docsFiles = docs
        .map((d, idx) => {
          const fid = extractFileId(d?.fileId || d?.id || d);
          if (!fid) return null;
          const caption = typeof d === "object" ? d?.caption || d?.name || null : null;
          return {
            uid: String(fid),
            fileId: String(fid),
            name: caption ? String(caption) : `document-${idx + 1}`,
            caption: caption ? String(caption) : undefined,
            status: "done",
          };
        })
        .filter(Boolean);
      setDocumentsFiles(docsFiles);
      syncDocumentsField(docsFiles);

      const ids = [
        ...new Set(
          [
            ...docsFiles.map((f) => extractFileId(f?.fileId || f?.uid)),
            ...(Array.isArray(res?.certifications) ? res.certifications.map((c) => extractFileId(c?.fileId)) : []),
          ].filter(Boolean),
        ),
      ];
      Promise.allSettled(ids.map((id) => fetchSignedUrlForFileId(id))).catch(() => null);
    } catch (err) {
      setError(extractBackendMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadSectors = async () => {
    setSectorsLoading(true);
    try {
      const res = await professionSectorService.list();
      const list = Array.isArray(res) ? res : [];
      setSectors(list);
      console.log("[helper-professions-form] sectors response", list);
    } catch (err) {
      console.error("[helper-professions-form] sectors fetch failed", err);
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  };

  const loadServiceOfferings = async () => {
    setServiceOfferingsLoading(true);
    try {
      const res = await olreadiaService.serviceOfferings.list({ page: 0, size: 200 });
      const content = Array.isArray(res?.content) ? res.content : Array.isArray(res) ? res : [];
      const options = content
        .map((s) => {
          const code = s?.code || s?.briefName || s?.name || s?.id;
          if (!code) return null;
          const label = s?.name ? `${s.name} (${code})` : String(code);
          return { label, value: String(code) };
        })
        .filter(Boolean);
      setServiceOfferingOptions(options);
    } catch (err) {
      console.error("[helper-professions-form] service offerings fetch failed", err);
      setServiceOfferingOptions([]);
    } finally {
      setServiceOfferingsLoading(false);
    }
  };

  useEffect(() => {
    loadSectors();
    loadServiceOfferings();
    if (mode === "create") {
      const initialProfileType = presetType || "GENERAL";
      form.setFieldsValue({
        id: undefined,
        profileType: initialProfileType,
        sectorCode: initialProfileType === "GENERAL" ? [] : "",
        profileName: "",
        profileSummary: "",
        description: "",
        experience: "",
        previousRegionsWorkedIn: [],
        references: [],
        workExperienceHistory: [],
        education: [],
        educationLevel: undefined,
        documents: [],
        certifications: [],
        interestedServices: [],
        interestedLocations: [],
        interestedShifts: [],
      });
      setDocumentsFiles([]);
      return;
    }

    if (seededProfile?.id && String(seededProfile.id) === String(professionProfileId)) {
      // Use seeded profile for initial fill; avoid a failing getById if backend has bad data.
      const docs = Array.isArray(seededProfile?.documents) ? seededProfile.documents : [];
      const docsAreObjects = docs.some((d) => d && typeof d === "object");
      setDocumentsUseObjects(docsAreObjects);
      form.setFieldsValue({
        id: seededProfile?.id,
        profileType: seededProfile?.profileType,
        sectorCode: seededProfile?.sector?.code || seededProfile?.sectorCode || "",
        profileName: seededProfile?.profileName || "",
        profileSummary: seededProfile?.profileSummary || "",
        description: seededProfile?.description || "",
        experience: seededProfile?.experience || "",
        previousRegionsWorkedIn: Array.isArray(seededProfile?.previousRegionsWorkedIn) ? seededProfile.previousRegionsWorkedIn : [],
        references: Array.isArray(seededProfile?.references) ? seededProfile.references : [],
        workExperienceHistory: Array.isArray(seededProfile?.workExperienceHistory) ? seededProfile.workExperienceHistory : [],
        education: Array.isArray(seededProfile?.education) ? seededProfile.education : [],
        educationLevel: seededProfile?.educationLevel || undefined,
        documents: docs,
        certifications: Array.isArray(seededProfile?.certifications) ? seededProfile.certifications : [],
        interestedServices: Array.isArray(seededProfile?.interestedServices)
          ? seededProfile.interestedServices.map((s) => s?.code || s?.name || s?.id).filter(Boolean)
          : [],
        interestedLocations: Array.isArray(seededProfile?.interestedLocations) ? seededProfile.interestedLocations : [],
        interestedShifts: Array.isArray(seededProfile?.interestedShifts) ? seededProfile.interestedShifts : [],
      });

      const docsFiles = docs
        .map((d, idx) => {
          const fid = extractFileId(d?.fileId || d?.id || d);
          if (!fid) return null;
          const caption = typeof d === "object" ? d?.caption || d?.name || null : null;
          return {
            uid: String(fid),
            fileId: String(fid),
            name: caption ? String(caption) : `document-${idx + 1}`,
            caption: caption ? String(caption) : undefined,
            status: "done",
          };
        })
        .filter(Boolean);
      setDocumentsFiles(docsFiles);
      syncDocumentsField(docsFiles);
      return;
    }

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, professionProfileId]);

  const onSave = async (values) => {
    if (!helperId) {
      message.error("Missing helperId in route");
      return;
    }

    const normalizeInterestedServices = (raw) => {
      if (!Array.isArray(raw)) return [];
      return raw
        .map((entry) => {
          if (!entry) return null;
          if (typeof entry === "string") {
            const code = entry.trim();
            return code ? { code } : null;
          }
          if (typeof entry === "object") {
            const code = String(entry.code || entry.id || entry.name || "").trim();
            return code ? { ...entry, code } : null;
          }
          return null;
        })
        .filter(Boolean);
    };

    const payloadBase = {
      id: values?.id || undefined,
      profileType: values?.profileType || undefined,
      profileName: values?.profileName?.trim() || undefined,
      profileSummary: values?.profileSummary?.trim() || undefined,
      description: values?.description?.trim() || undefined,
      experience: values?.experience?.trim() || undefined,
      previousRegionsWorkedIn: Array.isArray(values?.previousRegionsWorkedIn)
        ? values.previousRegionsWorkedIn.map((v) => String(v || "").trim()).filter(Boolean)
        : [],
      references: Array.isArray(values?.references) ? values.references : [],
      workExperienceHistory: Array.isArray(values?.workExperienceHistory) ? values.workExperienceHistory : [],
      education: Array.isArray(values?.education) ? values.education : [],
      // Keep key present so backend doesn't accidentally clear it when omitted.
      educationLevel: values?.educationLevel ?? null,
      documents: Array.isArray(values?.documents) ? values.documents : [],
      certifications: Array.isArray(values?.certifications) ? values.certifications : [],
      interestedServices: normalizeInterestedServices(values?.interestedServices),
      interestedLocations: Array.isArray(values?.interestedLocations) ? values.interestedLocations : [],
      interestedShifts: Array.isArray(values?.interestedShifts) ? values.interestedShifts : [],
    };

    const sectorCodes = (Array.isArray(values?.sectorCode) ? values.sectorCode : [values?.sectorCode])
      .map((v) => String(v || "").trim())
      .filter(Boolean);

    if (!payloadBase.profileType) return message.warning("Profile type is required");
    if (!sectorCodes.length) return message.warning("Sector is required");

    if (saving) return;
    setSaving(true);
    try {
      if (mode === "create") {
        const creatingMany = String(payloadBase.profileType || "").toUpperCase() === "GENERAL" && sectorCodes.length > 1;
        if (creatingMany) {
          // Create sequentially to avoid backend race conditions (user_profiles upsert/insert).
          for (const code of sectorCodes) {
            const payload = { ...payloadBase, sectorCode: code };
            // eslint-disable-next-line no-console
            console.log("[helper-professions-form] create payload", { helperId, payload });
            // eslint-disable-next-line no-await-in-loop
            await olreadiaService.professionProfiles.createForUser(helperId, payload);
          }
          message.success(`Created ${sectorCodes.length} profession profiles.`);
        } else {
          const payload = { ...payloadBase, sectorCode: sectorCodes[0] };
          console.log("[helper-professions-form] create payload", { helperId, payload });
          const created = await olreadiaService.professionProfiles.createForUser(helperId, payload);
          // eslint-disable-next-line no-console
          console.log("[helper-professions-form] create response", created);
          message.success("Profession profile created.");
        }
      } else {
        const payload = { ...payloadBase, sectorCode: sectorCodes[0] };
        console.log("[helper-professions-form] update payload", { helperId, payload });
        const updated = await olreadiaService.professionProfiles.updateForUser(helperId, payload);
        console.log("[helper-professions-form] update response", updated);
        message.success("Profession profile updated.");
      }
      navigate(-1);
    } catch (err) {
      console.error("[helper-professions-form] save failed", err);
      message.error(extractBackendMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sectionItems = [
    { key: "profile", label: "1) Profile" },
    { key: "experience", label: "2) Experience" },
    { key: "references", label: "3) References" },
    { key: "work", label: "4) Work History" },
    { key: "education", label: "5) Education" },
    { key: "documents", label: "6) Documents" },
    { key: "certifications", label: "7) Certifications" },
    { key: "interests", label: "8) Interests" },
  ];

  const sectorOptions = useMemo(() => {
    const options = (Array.isArray(sectors) ? sectors : [])
      .filter((s) => s?.code)
      .map((s) => ({
        label: `${s.name || s.code} (${s.code})`,
        value: s.code,
      }));

    const currentValue = form.getFieldValue("sectorCode");
    const current = Array.isArray(currentValue) ? "" : String(currentValue || "").trim();
    if (current && !options.some((o) => String(o.value).toLowerCase() === current.toLowerCase())) {
      options.unshift({ label: `${current} (custom)`, value: current });
    }

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectors, form]);

  const profileTypeValue = Form.useWatch("profileType", form);
  const allowMultiSector =
    mode === "create" && String(profileTypeValue || "").toUpperCase() === "GENERAL";

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const ids = sectionItems.map((s) => `profession-form-section-${s.key}`);

    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        const scrollTop = container.scrollTop || 0;

        // If the user clicked a section link, keep it highlighted while smooth-scrolling,
        // to avoid the highlight "walking" through intermediate sections.
        const pendingKey = pendingSectionRef.current;
        if (pendingKey && Date.now() < (pendingSectionUntilRef.current || 0)) {
          const targetEl = document.getElementById(
            `profession-form-section-${pendingKey}`,
          );
          if (targetEl) {
            const targetTop = targetEl.offsetTop - 8;
            if (Math.abs(targetTop - scrollTop) <= 36) {
              pendingSectionRef.current = null;
              pendingSectionUntilRef.current = 0;
            } else {
              setActiveSection(pendingKey);
              return;
            }
          } else {
            pendingSectionRef.current = null;
            pendingSectionUntilRef.current = 0;
          }
        }

        // Prefer a stable "current section" rule: pick the last section header
        // that has been scrolled past (with a small lookahead threshold).
        const threshold = 24; // px
        const cursor = scrollTop + threshold;

        const offsets = ids
          .map((id) => {
            const el = document.getElementById(id);
            if (!el) return null;
            return { key: id.replace("profession-form-section-", ""), top: el.offsetTop };
          })
          .filter(Boolean)
          .sort((a, b) => a.top - b.top);

        let nextKey = offsets[0]?.key || "profile";
        for (let i = 0; i < offsets.length; i += 1) {
          if (offsets[i].top <= cursor) nextKey = offsets[i].key;
          else break;
        }

        setActiveSection(nextKey);
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      container.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-fluid p-3">
      <Card style={{ borderRadius: 12 }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 10,
          justifyContent: "space-between",
          marginBottom: 12,
           }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Go Back
          </Button>
          <div style={{ flex:1, textAlign: "center"}}>
          <Text strong style={{ fontSize: 16 }}>
            {title}
          </Text>
          </div>
        </div>

        <Divider style={{ marginBlock: 12 }} />

        {error ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load"
            description={error}
            style={{ marginBottom: 12 }}
            action={
              <Button size="small" onClick={loadProfile}>
                Retry
              </Button>
            }
          />
        ) : null}

        <Spin spinning={loading}>
	          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
	            <div style={{ width: 240, position: "sticky", top: 8, alignSelf: "flex-start" }}>
	              <Card size="small" style={{ borderRadius: 12 }}>
	                <Space direction="vertical" size={6} style={{ width: "100%" }}>
	                  {sectionItems.map((s) => (
	                    <Button
	                      key={s.key}
	                      type="text"
	                      style={{
	                        textAlign: "left",
	                        paddingInline: 8,
	                        background: activeSection === s.key ? "#e6f4ff" : "transparent",
	                        borderRadius: 8,
	                        color: activeSection === s.key ? "#1677ff" : undefined,
	                      }}
	                      onClick={() => {
	                        const container = scrollRef.current;
	                        const el = document.getElementById(`profession-form-section-${s.key}`);
	                        if (!container || !el) return;
	                        pendingSectionRef.current = s.key;
	                        pendingSectionUntilRef.current = Date.now() + 1200;
	                        setActiveSection(s.key);
	                        container.scrollTo({ top: el.offsetTop - 8, behavior: "smooth" });
	                      }}
	                    >
	                      {s.label}
	                    </Button>
	                  ))}
	                </Space>
	              </Card>
	            </div>

            <div style={{ flex: 1, minWidth: 0, maxWidth: fullWidth ? 1280 : 980 }}>
              <Form layout="vertical" form={form} onFinish={onSave}>
                <Form.Item name="id" hidden>
                  <Input />
                </Form.Item>

                <div
                  ref={scrollRef}
                  style={{
                    maxHeight: "calc(100vh - 260px)",
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <div id="profession-form-section-profile" />
	                    <Card size="small" title="1) Profile" style={{ borderRadius: 12 }}>
	                      <Row gutter={[12, 12]}>
	                        <Col xs={24} md={12}>
	                          <Form.Item name="profileType" label="Profile Type" rules={[{ required: true }]}>
	                            <Select
	                              disabled={mode === "create" && Boolean(presetType)}
	                              options={[
	                                { label: "General", value: "GENERAL" },
	                                { label: "Specialized", value: "SPECIALIZED" },
	                              ]}
	                            />
	                          </Form.Item>
	                        </Col>
	                        <Col xs={24} md={12}>
	                          <Form.Item
	                            name="sectorCode"
	                            label="Sector"
	                            rules={[
	                              {
	                                validator: async (_, value) => {
	                                  if (allowMultiSector) {
	                                    if (Array.isArray(value) && value.filter(Boolean).length) return;
	                                    throw new Error("Select at least one sector");
	                                  }
	                                  if (value) return;
	                                  throw new Error("Sector is required");
	                                },
	                              },
	                            ]}
	                          >
	                            <Select
	                              mode={allowMultiSector ? "multiple" : undefined}
	                              showSearch
	                              allowClear
	                              loading={sectorsLoading}
	                              options={sectorOptions}
	                              placeholder="Select a profession sector"
	                              filterOption={(input, option) =>
	                                String(option?.label || "")
	                                  .toLowerCase()
	                                  .includes(String(input || "").toLowerCase())
	                              }
	                            />
	                          </Form.Item>
	                        </Col>
	                      </Row>
                      <Form.Item name="profileName" label="Profile Name">
                        <Input />
                      </Form.Item>
                      <Form.Item name="profileSummary" label="Profile Summary">
                        <Input.TextArea rows={2} />
                      </Form.Item>
                      <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Card>

                    <div id="profession-form-section-experience" />
                    <Card size="small" title="2) Experience" style={{ borderRadius: 12 }}>
                      <Form.Item name="experience" label="Experience">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                      <Form.Item name="previousRegionsWorkedIn" label="Previous Regions Worked In">
                        <GooglePlacesTagsSelect
                          googleMapsApiKey={googleMapsApiKey}
                          placeholder="Add regions..."
                          types={["(cities)"]}
                          resolveValueFromDetails={regionSummaryFromPlaceDetails}
                        />
                      </Form.Item>
                    </Card>

                    <div id="profession-form-section-references" />
                    <Card size="small" title="3) References" style={{ borderRadius: 12 }}>
                      <Form.List name="references">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ borderRadius: 12 }}
                                extra={
                                  <Button danger type="text" onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                }
                              >
                                <Row gutter={[12, 12]}>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "name"]} label="Name">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "relationship"]} label="Relationship">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "phone"]} label="Phone">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "email"]} label="Email">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24}>
                                    <Form.Item {...field} name={[field.name, "notes"]} label="Notes">
                                      <Input.TextArea rows={2} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                              Add reference
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    </Card>

                    <div id="profession-form-section-work" />
                    <Card size="small" title="4) Work Experience History" style={{ borderRadius: 12 }}>
                      <Form.List name="workExperienceHistory">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ borderRadius: 12 }}
                                extra={
                                  <Button danger type="text" onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                }
                              >
                                <Row gutter={[12, 12]}>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "organization"]} label="Organization">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "role"]} label="Role">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "location"]} label="Location">
                                      <GooglePlacesAutocompleteInput
                                        googleMapsApiKey={googleMapsApiKey}
                                        placeholder="Search work location"
                                        types={["geocode"]}
                                        onResolvePlace={async ({ details, prediction }) => {
                                          const resolved =
                                            details?.formatted_address ||
                                            prediction?.description ||
                                            "";
                                          form.setFieldValue(
                                            ["workExperienceHistory", field.name, "location"],
                                            resolved,
                                          );
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item
                                      {...field}
                                      name={[field.name, "startDate"]}
                                      label="Start Date"
                                      getValueProps={(value) => ({
                                        value: value ? dayjs(String(value)) : null,
                                      })}
                                      getValueFromEvent={(value) =>
                                        value ? value.format("YYYY-MM-DD") : ""
                                      }
                                    >
                                      <DatePicker style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <Form.Item
                                      {...field}
                                      name={[field.name, "endDate"]}
                                      label="End Date"
                                      getValueProps={(value) => ({
                                        value: value ? dayjs(String(value)) : null,
                                      })}
                                      getValueFromEvent={(value) =>
                                        value ? value.format("YYYY-MM-DD") : ""
                                      }
                                    >
                                      <DatePicker style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "current"]} valuePropName="checked">
                                      <Checkbox>Current</Checkbox>
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24}>
                                    <Form.Item {...field} name={[field.name, "notes"]} label="Notes">
                                      <Input.TextArea rows={2} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                              Add work experience
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    </Card>

                    <div id="profession-form-section-education" />
                    <Card size="small" title="5) Education" style={{ borderRadius: 12 }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <Form.Item name="educationLevel" label="Education Level">
                            <Select
                              allowClear
                              options={[
                                { label: "None", value: "NONE" },
                                { label: "High School", value: "HIGH_SCHOOL" },
                                { label: "GED", value: "GED" },
                                { label: "Certificate", value: "CERTIFICATE" },
                                { label: "Associate", value: "ASSOCIATE" },
                                { label: "Bachelor", value: "BACHELOR" },
                                { label: "Master", value: "MASTER" },
                                { label: "Doctorate", value: "DOCTORATE" },
                                { label: "Other", value: "OTHER" },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.List name="education">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ borderRadius: 12 }}
                                extra={
                                  <Button danger type="text" onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                }
                              >
                                <Row gutter={[12, 12]}>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "institution"]} label="Institution">
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item {...field} name={[field.name, "program"]} label="Program">
                                      <Input />
                                    </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item {...field} name={[field.name, "educationLevel"]} label="Education Level">
                                  <Select
                                    allowClear
                                    placeholder="Select level"
                                    options={[
                                      { value: "HIGH_SCHOOL", label: "High School" },
                                      { value: "VOCATIONAL_CERTIFICATE", label: "Vocational Certificate" },
                                      { value: "DIPLOMA_ADVANCED_DIPLOMA", label: "Diploma / Advanced Diploma" },
                                      { value: "ASSOCIATE_DEGREE", label: "Associate Degree" },
                                      { value: "BACHELOR_DEGREE", label: "Bachelor's Degree" },
                                      { value: "POSTGRADUATE_CERTIFICATE", label: "Postgraduate Certificate" },
                                      { value: "MASTER_DEGREE", label: "Master's Degree" },
                                      { value: "DOCTORATE", label: "Doctorate" },
                                      { value: "PROFESSIONAL_CERTIFICATION", label: "Professional Certification" },
                                      { value: "OTHER", label: "Other" },
                                    ]}
                                  />
                                </Form.Item>
                              </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item
                                      {...field}
                                      name={[field.name, "graduationYear"]}
                                      label="Graduation Year"
                                      getValueProps={(value) => ({
                                        value: value ? dayjs(String(value), "YYYY") : null,
                                      })}
                                      getValueFromEvent={(value) =>
                                        value ? value.format("YYYY") : ""
                                      }
                                    >
                                      <DatePicker picker="year" format="YYYY" style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24}>
                                    <Form.Item {...field} name={[field.name, "notes"]} label="Notes">
                                      <Input.TextArea rows={2} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                              Add education
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    </Card>

                    <div id="profession-form-section-documents" />
                    <Card size="small" title="6) Documents" style={{ borderRadius: 12 }}>
                      <Form.Item name="documents" label="Documents">
                        <Upload
                          multiple
                          listType="picture-card"
                          fileList={documentsFiles.map((f) => {
                            const fid = extractFileId(f?.fileId || f?.uid);
                            const signed = fid ? signedUrlByFileId?.[String(fid)] : null;
                            return { ...f, url: signed || f.url, thumbUrl: signed || f.thumbUrl };
                          })}
                          customRequest={async ({ file, onSuccess, onError }) => {
                            try {
                              setDocumentsUploading(true);
                              const uploaderId = ensureUploaderId();
                              if (!uploaderId) throw new Error("Missing uploader id");
                              const res = await uploadFile(uploaderId, file, { purpose: "USER_PROFILE", fileType: "DOCUMENT" });
                              const fid = res?.fileId;
                              if (!fid) throw new Error("Upload failed: missing fileId");
                              const next = [...(Array.isArray(documentsFiles) ? documentsFiles : []), { uid: String(fid), fileId: String(fid), name: file?.name || "document", status: "done" }];
                              setDocumentsFiles(next);
                              syncDocumentsField(next);
                              await fetchSignedUrlForFileId(fid);
                              onSuccess?.(res);
                              message.success(`${file?.name || "Document"} uploaded`);
                            } catch (err) {
                              console.error("[helper-professions-form] document upload failed", err);
                              message.error(err?.message || "Failed to upload document");
                              onError?.(err);
                            } finally {
                              setDocumentsUploading(false);
                            }
                          }}
                          onRemove={(file) => {
                            const uid = String(file?.uid || "");
                            const next = (Array.isArray(documentsFiles) ? documentsFiles : []).filter((f) => String(f?.uid) !== uid);
                            setDocumentsFiles(next);
                            syncDocumentsField(next);
                            return true;
                          }}
                          onPreview={(file) => openAttachmentPreview(file?.fileId || file?.uid, file?.name || "Document")}
                        >
                          <div style={{ opacity: documentsUploading ? 0.6 : 1 }}>
                            <PlusOutlined />
                            <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                          </div>
                        </Upload>
                      </Form.Item>
                    </Card>

                    <div id="profession-form-section-certifications" />
                    <Card size="small" title="7) Certifications" style={{ borderRadius: 12 }}>
                      <Form.List name="certifications">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            {fields.map((field) => {
                              const current = form.getFieldValue(["certifications", field.name]) || {};
                              const fileId = extractFileId(current?.fileId);
                              return (
                                <Card
                                  key={field.key}
                                  size="small"
                                  style={{ borderRadius: 12 }}
                                  title={current?.name || `Certification ${field.name + 1}`}
                                  extra={
                                    <Button danger type="text" onClick={() => remove(field.name)}>
                                      Remove
                                    </Button>
                                  }
                                >
                                  <Row gutter={[12, 12]}>
                                    <Col xs={24} md={12}>
                                      <Form.Item {...field} name={[field.name, "name"]} label="Name">
                                        <Input />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <Form.Item {...field} name={[field.name, "issuer"]} label="Issuer">
                                        <Input />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <Form.Item {...field} name={[field.name, "certificationNumber"]} label="Certification Number">
                                        <Input />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={6}>
                                      <Form.Item
                                        {...field}
                                        name={[field.name, "issueDate"]}
                                        label="Issue Date"
                                        getValueProps={(value) => ({
                                          value: value ? dayjs(String(value)) : null,
                                        })}
                                        getValueFromEvent={(value) =>
                                          value ? value.format("YYYY-MM-DD") : ""
                                        }
                                      >
                                        <DatePicker style={{ width: "100%" }} />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={6}>
                                      <Form.Item
                                        {...field}
                                        name={[field.name, "expiryDate"]}
                                        label="Expiry Date"
                                        getValueProps={(value) => ({
                                          value: value ? dayjs(String(value)) : null,
                                        })}
                                        getValueFromEvent={(value) =>
                                          value ? value.format("YYYY-MM-DD") : ""
                                        }
                                      >
                                        <DatePicker style={{ width: "100%" }} />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                      <Form.Item {...field} name={[field.name, "fileId"]} label="Certificate File">
                                        <Space wrap>
                                          {renderAttachmentValue(fileId, current?.name || "Certification")}
                                          <Upload
                                            showUploadList={false}
                                            customRequest={async ({ file, onSuccess, onError }) => {
                                              try {
                                                const uploaderId = ensureUploaderId();
                                                if (!uploaderId) throw new Error("Missing uploader id");
                                                const res = await uploadFile(uploaderId, file, { purpose: "USER_PROFILE", fileType: "DOCUMENT" });
                                                const fid = res?.fileId;
                                                if (!fid) throw new Error("Upload failed: missing fileId");
                                                form.setFieldValue(["certifications", field.name, "fileId"], String(fid));
                                                await fetchSignedUrlForFileId(fid);
                                                onSuccess?.(res);
                                                message.success(`${file?.name || "File"} uploaded`);
                                              } catch (err) {
                                                console.error("[helper-professions-form] cert upload failed", err);
                                                message.error(err?.message || "Failed to upload");
                                                onError?.(err);
                                              }
                                            }}
                                          >
                                            <Button size="small" icon={<PlusOutlined />}>Upload file</Button>
                                          </Upload>
                                        </Space>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Card>
                              );
                            })}
                            <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                              Add certification
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    </Card>

                    <div id="profession-form-section-interests" />
                    <Card size="small" title="8) Interests" style={{ borderRadius: 12 }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name="interestedServices"
                            label="Interested Services (codes)"
                          >
                            <Select
                              mode="multiple"
                              allowClear
                              loading={serviceOfferingsLoading}
                              options={serviceOfferingOptions}
                              showSearch
                              placeholder="Select service codes"
                              filterOption={(input, option) =>
                                String(option?.label || "")
                                  .toLowerCase()
                                  .includes(String(input || "").toLowerCase())
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="interestedShifts" label="Shift Availability">
                            <Select
                              mode="multiple"
                              allowClear
                              options={[
                                { label: "Single", value: "SINGLE" },
                                { label: "Double", value: "DOUBLE" },
                                { label: "Partial", value: "PARTIAL" },
                                { label: "Open", value: "OPEN" },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.List name="interestedLocations">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            <Space style={{ width: "100%", justifyContent: "space-between" }}>
                              <Text strong>Interested Locations</Text>
                              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add({ open: true })}>
                                Add location
                              </Button>
                            </Space>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ borderRadius: 12 }}
                              >
                                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button danger type="text" onClick={() => remove(field.name)}>
                                      Remove
                                    </Button>
                                  </div>

                                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      Search or enter places like city, county, state, country.
                                    </Text>
                                    <GooglePlacesAutocompleteInput
                                      googleMapsApiKey={googleMapsApiKey}
                                      placeholder="Search interested location"
                                      onResolvePlace={async ({ details }) => {
                                        const next = locationFieldsFromPlaceDetails(details);
                                        form.setFieldValue(["interestedLocations", field.name, "city"], next.city || "");
                                        form.setFieldValue(["interestedLocations", field.name, "state"], next.state || "");
                                        form.setFieldValue(["interestedLocations", field.name, "county"], next.county || "");
                                        form.setFieldValue(["interestedLocations", field.name, "country"], next.country || "USA");
                                      }}
                                    />
                                  </div>

                                  <Form.Item {...field} name={[field.name, "city"]} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item {...field} name={[field.name, "state"]} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item {...field} name={[field.name, "county"]} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item {...field} name={[field.name, "country"]} hidden>
                                    <Input />
                                  </Form.Item>

                                  <Form.Item {...field} name={[field.name, "open"]} valuePropName="checked" style={{ marginBottom: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                      <Text>Open to this location</Text>
                                      <Switch />
                                    </div>
                                  </Form.Item>
                                </Space>
                              </Card>
                            ))}
                          </Space>
                        )}
                      </Form.List>
                    </Card>
                  </Space>
                </div>

                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    background: "#fff",
                    paddingTop: 12,
                    paddingBottom: 4,
                    borderTop: "1px solid #f0f0f0",
                    marginTop: 12,
                  }}
                >
                  <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                    <Button onClick={() => navigate(-1)} disabled={saving}>
                      Cancel
                    </Button>
                    <Button type="primary" loading={saving} disabled={saving} onClick={() => form.submit()}>
                      Save
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          </div>
        </Spin>
      </Card>

      <Modal
        open={previewOpen}
        title={previewTitle}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={900}
        zIndex={100500}
        destroyOnClose
        getContainer={() => document.body}
      >
        <div style={{ textAlign: "center" }}>
          <Image
            src={previewUrl}
            alt={previewTitle}
            style={{ maxHeight: "70vh", objectFit: "contain" }}
          />
        </div>
      </Modal>
    </div>
  );
}
